import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Button from "@material-ui/core/Button";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import { setAppLanguage } from "../../appUtils";
import { ReactComponent as LanguageIcon } from "../../assets/language.svg";
import { getLocales } from "../../store/i18n/selectors";
import { putUserLangAction } from "../../store/user/actions";

const menuId = "language-switcher";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
    },
    leftIcon: {
      marginRight: "2px",
    },
    selectedLang: {
      display: "none",
      [theme.breakpoints.up("md")]: {
        display: "inline",
      },
    },
  })
);

interface LanguageSwitcherProps {
  component?: React.ElementType;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  component: Component = Button,
}) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation("global");
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const locales = useSelector(getLocales);
  const dispatch = useDispatch();

  const handleMenu = (event: React.ChangeEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const changeLanguage = (newLanguage: string) => {
    if (i18n.language !== newLanguage) {
      setAppLanguage(newLanguage);
      dispatch(putUserLangAction(newLanguage));
    }
    setAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <ClickAwayListener mouseEvent="onMouseDown" onClickAway={handleClose}>
      <div className={classes.root}>
        <Component
          aria-label={t("sidebar.changeLanguage")}
          aria-haspopup="true"
          color="inherit"
          onClick={handleMenu}
          aria-controls={menuId}
          data-testid={`${menuId}-button`}
        >
          <svg height="30" width="40">
            <LanguageIcon />
          </svg>
          <span
            data-testid={`${menuId}-current-language`}
            className={classes.selectedLang}
          >
            {t("languages." + i18n.language)}
          </span>
        </Component>
        <Menu
          id={menuId}
          data-testid={`${menuId}-menu`}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={open}
          onClose={handleClose}
        >
          {locales.map(({ code }) => (
            <MenuItem
              key={code}
              selected={code === i18n.language}
              onClick={() => changeLanguage(code)}
              data-testid={`${code}-item`}
            >
              {t("languages." + code)}
            </MenuItem>
          ))}
        </Menu>
      </div>
    </ClickAwayListener>
  );
};

export default LanguageSwitcher;
